// Pending-attachment chip in the composer — thumbnail preview for images (from
// the picked file in memory, so no request), type-aware icon badge for
// everything else, name, size, and remove button.
import { X } from 'lucide-react';
import type { ChatAttachment } from '@/lib/chatStore';
import { getFileTypeStyle } from '@/lib/fileTypeIcon';
import { formatBytes as formatFileSize } from '@/lib/format';

export function AttachmentPill({ attachment, onRemove }: { attachment: ChatAttachment; onRemove: () => void }) {
  const mime = attachment.mimeType;
  const isImage = mime.startsWith('image/');
  const { Icon, color, bg, label } = getFileTypeStyle(mime);

  return (
    <div className="group flex items-center gap-1.5 rounded-lg border border-border bg-card px-2 py-1">
      {isImage && attachment.localUrl ? (
        <img src={attachment.localUrl} alt={attachment.name} className="h-8 w-8 rounded object-cover" />
      ) : (
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded ${bg} text-2xs font-bold leading-none ${color}`}
          aria-hidden="true"
        >
          {/* Show label text for well-known types, icon for everything else */}
          {label !== 'FILE' && label !== 'CODE' && label !== 'IMG' ? label : <Icon className="h-4 w-4" />}
        </div>
      )}
      <div className="flex min-w-0 flex-col">
        <span className="max-w-[120px] truncate text-xs font-medium text-foreground">{attachment.name}</span>
        <span className="text-xs text-muted-foreground">{formatFileSize(attachment.size)}</span>
      </div>
      <button
        type="button"
        className="shrink-0 rounded-sm p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100 touch:opacity-100"
        onClick={onRemove}
        title="Remove attachment"
        aria-label={`Remove ${attachment.name}`}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
